import { Box, Button, ButtonProps, Typography } from '@mui/material'
import { useRootLoaderData } from 'components/Root.loader'
import { ORDERED_AUTHORITIES, AUTHORITIES_FROM_JP, AUTHORITIES } from 'config'
import { useRef, ChangeEvent, useState } from 'react'
import { Authority, AddUserPayload } from 'types'
import { parseCsv, ColumnsMismatchError, exceedsUserCountCheck } from 'utils/csv'
import { readFileTextContent } from 'utils/file'

import { useLoaderData } from './UsersConfig.loader'

function isAuthority(s: string): s is Authority {
  return ORDERED_AUTHORITIES.includes(s as Authority)
}

function alignDataRow(row: string[], useConcur: boolean): string[] {
  if (useConcur) {
    return row
  }

  // add blank Concur ID
  const [name, email, ...rest] = row
  return [name, email, '', ...rest]
}

const AUTH_DICT = {
  ...AUTHORITIES_FROM_JP,
  一般ユーザー: AUTHORITIES.REGISTER,
}

function usersFromData(
  csvData: string[][],
  useConcur: boolean,
  subGroupNames: string[],
): AddUserPayload[] {
  const [, ...rows] = csvData
  const groupSet = new Set(subGroupNames)

  return rows.map((row, i) => {
    const [name, email, concurID, authority, ...subGroups] = alignDataRow(row, useConcur)

    // transform jp value like 'システム管理者' to keys like 'admin'
    const authorityKey = AUTH_DICT[authority] || authority

    if (!isAuthority(authorityKey)) {
      throw new Error(`第${i + 2}行の権限の値が無効です`)
    }

    const nonBlankGroups = subGroups.filter(Boolean)
    if (nonBlankGroups.some((group) => !groupSet.has(group))) {
      throw new Error(`第${i + 2}行のグループの値が無効です`)
    }

    return {
      name,
      email,
      concurID,
      authority: authorityKey,
      subGroups: nonBlankGroups,
    }
  })
}

type UIUBProps = {
  users2Add: AddUserPayload[]
  setUsers2Add: (a: AddUserPayload[]) => void
}
export default function UsersImportUploadBlock({ users2Add, setUsers2Add }: UIUBProps) {
  const userGroupConfig = useRootLoaderData().userGroupConfig
  const { useConcur, maximumCount } = userGroupConfig
  const { users, subGroupNames: subGroups } = useLoaderData()
  const [filename, setFilename] = useState('')
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const currentUsersCount = users.length

  const onClick: ButtonProps['onClick'] = () => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  const handleChangeFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event
    if (!target.files || target.files.length === 0) {
      return
    }

    const file = target.files[0]

    try {
      const data = await readFileTextContent(file)
      const csvData = parseCsv(data)
      const users = usersFromData(csvData, useConcur, subGroups)

      exceedsUserCountCheck(users.length, currentUsersCount, maximumCount)

      setUsers2Add(users)
      setFilename(file.name)
      setError('')
    } catch (error: any) {
      setUsers2Add([])
      setFilename('')
      setError(errorMsg(error))
    } finally {
      target.value = ''
    }
  }

  return (
    <Box display="flex" alignItems="center">
      <Box display="flex" marginRight={1}>
        <Button
          type="button"
          variant="contained"
          color="inherit"
          onClick={onClick}
          data-testid="select-file"
        >
          ファイルを選択
        </Button>
      </Box>
      {users2Add.length !== 0 && `${filename} （追加ユーザー数：${users2Add.length}）`}
      {error && (
        <Typography variant="body2" color="error">
          アップロードエラー：
          <br />
          {error}
        </Typography>
      )}
      <input ref={inputRef} style={{ display: 'none' }} type="file" onChange={handleChangeFile} />
    </Box>
  )
}

function errorMsg(error: Error): string {
  if (error instanceof ColumnsMismatchError) {
    return '入力列が正しくありません。'
  }

  return error.message
}
